import React, { useContext, useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Typography,
    Alert,
} from '@mui/material';
import { ThemeContext } from 'styled-components';
import { CsrfContext } from '../../../providers/CsrfProvider';
import { updateBillRule } from '../../../services/bill/BillRules';
import { useQueryClient } from 'react-query';

const EditPercentRulePercentageForm = ({ templateId, rule, open, onClose, onSuccess }) => {
    const theme = useContext(ThemeContext);
    const { csrfToken } = useContext(CsrfContext);
    const queryClient = useQueryClient();

    const [rulePercentage, setRulePercentage] = useState('');
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    React.useEffect(() => {
        if (rule && open) {
            setRulePercentage(rule.rule_percentage ? rule.rule_percentage.toString() : '');
            setError(null);
        }
    }, [rule, open]);

    const handleClose = () => {
        setRulePercentage('');
        setError(null);
        onClose();
    };

    const handleSubmit = async () => {
        setError(null);

        const numValue = parseFloat(rulePercentage);
        if (isNaN(numValue) || numValue <= 0 || numValue > 100) {
            setError('Rule percentage must be between 0 and 100');
            return;
        }

        setSubmitting(true);

        try {
            // Get current participants and percentages
            const participantUserIDs = [];
            const participantPercentages = {};

            if (rule.participants && rule.participants.length > 0) {
                rule.participants.forEach(p => {
                    if (p.user && p.user.id) {
                        participantUserIDs.push(p.user.id);
                        if (p.percentage != null) {
                            participantPercentages[p.user.id] = p.percentage;
                        }
                    }
                });
            }

            const ruleData = {
                rule_type: 'percent',
                rule_percentage: numValue,
                participant_user_ids: participantUserIDs,
                participant_percentages: participantPercentages,
            };

            await updateBillRule(String(templateId), rule.id, ruleData, csrfToken);

            queryClient.invalidateQueries(['billRules', String(templateId)]);
            queryClient.invalidateQueries(['estimatedSplits', String(templateId)]);

            if (onSuccess) {
                onSuccess();
            }

            handleClose();
        } catch (err) {
            console.error('Error updating rule percentage:', err);
            setError(err.response?.data?.error || err.message || 'Failed to update rule percentage');
        } finally {
            setSubmitting(false);
        }
    };

    if (!rule) {
        return null;
    }

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ color: theme.colors.text }}>
                Edit Rule Percentage
            </DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    {error && (
                        <Alert severity="error" onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}

                    <TextField
                        label="Rule Percentage (of bill total)"
                        type="number"
                        value={rulePercentage}
                        onChange={(e) => {
                            const value = e.target.value;
                            if (value === '' || (!isNaN(value) && parseFloat(value) >= 0 && parseFloat(value) <= 100)) {
                                setRulePercentage(value);
                            }
                        }}
                        fullWidth
                        inputProps={{ min: 0, max: 100, step: 0.01 }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                fontSize: '16px', // Minimum 16px to prevent iOS zoom
                            },
                            '& .MuiInputBase-input': {
                                color: theme.colors.text,
                                fontSize: '16px', // Minimum 16px to prevent iOS zoom
                            },
                            '& .MuiInputLabel-root': {
                                color: theme.colors.text,
                                fontSize: '16px', // Minimum 16px to prevent iOS zoom
                            },
                        }}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} sx={{ color: theme.colors.text }}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={submitting}
                    sx={{
                        backgroundColor: theme.colors.primary,
                        color: theme.colors.buttonText,
                        '&:hover': {
                            backgroundColor: theme.colors.primaryHover,
                        },
                    }}
                >
                    {submitting ? 'Updating...' : 'Update'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditPercentRulePercentageForm;
